import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

class CartUtils {
  constructor() {
    this.storageKey = 'user_cart';
    this.tempStorageKey = 'temp_cart';
  }

  /**
   * Get cart from localStorage
   */
  getCart() {
    try {
      const cartData = localStorage.getItem(this.storageKey);
      return cartData ? JSON.parse(cartData) : this.getEmptyCart();
    } catch (error) {
      console.error('Error getting cart:', error);
      return this.getEmptyCart();
    }
  }

  /**
   * Get temp cart from localStorage
   */
  getTempCart() {
    try {
      const cartData = localStorage.getItem(this.tempStorageKey);
      return cartData ? JSON.parse(cartData) : this.getEmptyCart();
    } catch (error) {
      console.error('Error getting temp cart:', error);
      return this.getEmptyCart();
    }
  }

  /**
   * Save cart to localStorage
   */
  saveCart(cart) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(cart));
      bus.emit('cart_update_available', { cart });
      return true;
    } catch (error) {
      console.error('Error saving cart:', error);
      return false;
    }
  }

  /**
   * Save temp cart to localStorage
   */
  saveTempCart(cart) {
    try {
      localStorage.setItem(this.tempStorageKey, JSON.stringify(cart));
      bus.emit('cart_temp_update_available', { cart });
      return true;
    } catch (error) {
      console.error('Error saving temp cart:', error);
      return false;
    }
  }

  /**
   * Get empty cart structure
   */
  getEmptyCart() {
    return {
      items: [],
      storeId: null,
      totalAmount: 0,
      totalDiscount: 0,
      deliveryAmount: 0,
      riderCommission: 0,
      paymentFee: 0,
      distanceKm: 0,
      grandTotal: 0,
      deliveryLocation: null,
      promoCode: null
    };
  }

  /**
   * Get applied promo code from cart
   */
  getAppliedPromoCode() {
    const cart = this.getCart();
    return cart.promoCode || null;
  }

  /**
   * Check if an item is already in cart
   */
  isAddedToCart(itemId) {
    const cart = this.getCart();
    return cart.items.some(item => item.id === itemId);
  }

  /**
   * Add item to cart
   */
  addItemToCart(item, quantity = 1) {
    const cart = this.getCart();

    // Check if adding item from different restaurant
    if (cart.storeId && cart.storeId !== item.storeId && cart.items.length > 0) {
      bus.emit('cart_different_restaurant', {
        currentStoreId: cart.storeId,
        newStoreId: item.storeId
      });

      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Erreur',
        body: 'Vous ne pouvez pas ajouter des articles de restaurants différents',
        type: 'error'
      });

      return {
        success: false,
        reason: 'different_restaurant',
        cart,
        tempCart: this.getTempCart()
      };
    }

    // Check if item already exists in cart
    const existingItemIndex = cart.items.findIndex(cartItem => cartItem.id === item.id);

    if (existingItemIndex !== -1) {
      // Update quantity of existing item
      cart.items[existingItemIndex].quantity += quantity;
      cart.items[existingItemIndex].totalPrice =
        (cart.items[existingItemIndex].price || cart.items[existingItemIndex].discountedPrice) *
        cart.items[existingItemIndex].quantity;
    } else {
      // Add new item
      const cartItem = {
        id: item.id,
        name: item.name,
        price: item.discountedPrice || item.price,
        quantity: quantity,
        totalPrice: (item.discountedPrice || item.price) * quantity,
        optionsTotal: item.optionsTotal || 0,
        drinksTotal: item.drinksTotal || 0,
        principalImageUrl: item.principalImageUrl,
        storeId: item.storeId,
        storeName: item.storeName,
        isDeal: item.isDeal || false
      };

      cart.items.push(cartItem);
    }

    // Update cart totals
    cart.storeId = item.storeId;
    cart.storeName = item.storeName;
    this.recalculateCartTotals(cart);

    // Save cart
    this.saveCart(cart);

    return {
      success: true,
      cart,
      tempCart: this.getTempCart()
    };
  }

  /**
   * Remove item from cart
   */
  removeItemFromCart(itemId) {
    const cart = this.getCart();
    const itemIndex = cart.items.findIndex(item => item.id === itemId);

    if (itemIndex !== -1) {
      const removedItem = cart.items.splice(itemIndex, 1)[0];

      // Move to temp cart
      const tempCart = this.getTempCart();
      tempCart.items.push(removedItem);
      this.saveTempCart(tempCart);

      // Recalculate totals
      if (cart.items.length === 0) {
        this.emptyCart();
      } else {
        this.recalculateCartTotals(cart);
        this.saveCart(cart);
      }

      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Info',
        body: 'Article déplacé vers le panier temporaire',
        type: 'info'
      });

      return {
        success: true,
        cart,
        tempCart
      };
    }

    return {
      success: false,
      reason: 'item_not_found',
      cart,
      tempCart: this.getTempCart()
    };
  }

  /**
   * Increase item quantity
   */
  increaseCartItem(itemId) {
    const cart = this.getCart();
    const item = cart.items.find(item => item.id === itemId);

    if (item) {
      item.quantity += 1;
      item.totalPrice = item.price * item.quantity;
      this.recalculateCartTotals(cart);
      this.saveCart(cart);

      return {
        success: true,
        cart
      };
    }

    return {
      success: false,
      reason: 'item_not_found',
      cart
    };
  }

  /**
   * Decrease item quantity
   */
  decreaseCartItem(itemId) {
    const cart = this.getCart();
    const item = cart.items.find(item => item.id === itemId);

    if (item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
        item.totalPrice = item.price * item.quantity;
        this.recalculateCartTotals(cart);
        this.saveCart(cart);
      } else {
        return this.removeItemFromCart(itemId);
      }

      return {
        success: true,
        cart
      };
    }

    return {
      success: false,
      reason: 'item_not_found',
      cart
    };
  }

  /**
   * Recalculate cart totals
   */
  recalculateCartTotals(cart, feesConfig = null, distanceKm = 0) {
    // Calculate items total
    cart.totalAmount = cart.items.reduce((sum, item) => {
      return sum + item.totalPrice + (item.optionsTotal || 0) + (item.drinksTotal || 0);
    }, 0);

    // Calculate delivery amount (if fees config provided)
    if (feesConfig) {
      const freeDeliveryThreshold = feesConfig.freeDeliveryThreshold || 10000;
      if (cart.totalAmount >= freeDeliveryThreshold) {
        cart.deliveryAmount = 0;
      } else {
        cart.deliveryAmount = feesConfig.baseFees + (distanceKm * feesConfig.costPerKm);
      }

      // Calculate rider commission
      cart.riderCommission = cart.deliveryAmount * (feesConfig.riderCommissionRate || 0.75);

      // Calculate payment fee (2%, min 100, max 500)
      const merchantAmount = cart.totalAmount - cart.totalDiscount;
      const grandTotalBeforePaymentFee = merchantAmount + cart.deliveryAmount;
      const paymentFeeRate = feesConfig.paymentFeeRate || 0.02;
      const paymentFeeMin = feesConfig.paymentFeeMin || 100;
      const paymentFeeMax = feesConfig.paymentFeeMax || 500;

      cart.paymentFee = Math.min(
        paymentFeeMax,
        Math.max(
          paymentFeeMin,
          Math.round(grandTotalBeforePaymentFee * paymentFeeRate)
        )
      );
    }

    // Calculate grand total
    cart.grandTotal = cart.totalAmount - cart.totalDiscount + cart.deliveryAmount + cart.paymentFee;

    return cart;
  }

  /**
   * Empty cart
   */
  emptyCart() {
    const emptyCart = this.getEmptyCart();
    this.saveCart(emptyCart);

    const emptyTempCart = this.getEmptyCart();
    this.saveTempCart(emptyTempCart);

    bus.emit('cart_empty', {});

    return {
      success: true,
      cart: emptyCart,
      tempCart: emptyTempCart
    };
  }

  /**
   * Set delivery location
   */
  setDeliveryLocation(location) {
    const cart = this.getCart();
    cart.deliveryLocation = location;
    this.saveCart(cart);

    bus.emit('cart_delivery_location_setted', { location });

    return {
      success: true,
      cart
    };
  }

  /**
   * Apply promo code
   */
  applyPromoCode(promoCodeData) {
    const cart = this.getCart();

    if (!promoCodeData || !promoCodeData.code) {
      return {
        success: false,
        reason: 'invalid_promo_code',
        cart
      };
    }

    cart.promoCode = promoCodeData;

    // Calculate discount
    if (promoCodeData.type === 'percentage') {
      cart.totalDiscount = Math.round(cart.totalAmount * (promoCodeData.discount / 100));
    } else if (promoCodeData.type === 'fixed') {
      cart.totalDiscount = promoCodeData.discount;
    }

    this.recalculateCartTotals(cart);
    this.saveCart(cart);

    bus.emit('cart_promo_code_applied', { promoCode: promoCodeData });

    return {
      success: true,
      cart
    };
  }
}

// Export singleton instance
const cartUtils = new CartUtils();
export default cartUtils;
